import { visit } from 'unist-util-visit'
import type { Plugin } from 'unified'
import type { HastRoot } from '@/types'

/**
 * Plugin to find all elements in the tree of type `raw` where the value contains `<scalar-`
 * and hoist them to be direct descendants of the root.
 *
 * This also searches for scalar elements inside raw node values, and handles nested structures.
 * When finding a closing scalar tag, it will hoist that tag and all following siblings
 * to be level-1 children of the root after their current level-1 parent.
 */
export function hoistScalarElements(): Plugin {
  const closingTag = /<\/(scalar-[a-zA-Z0-9-]+)>/g

  return () => (tree: any) => {
    if (!tree || tree.type !== 'root') {
      return tree
    }

    const root = tree as HastRoot
    let currentFirstGenChildIndex = -1

    visit(tree, (node, index, parent) => {
      /**
       * If this is a first-generation child, we set currentFirstGenChildIndex
       * so that later, if we are in a lower-generation node, we can reference the
       * current first-generation child index.
       * After that, we skip this node. because any tag already at level 1 is OK.
       */
      if (parent?.type === 'root') currentFirstGenChildIndex = index as number
      if (node?.type !== 'raw') return
      /**
       * If we find a closing tag that matches the last opening tag in the stack,
       * then we want to slice this node and all of its following siblings out of their
       * parent, and insert them as children of the root node after the current first-gen child.
       *
       * We only get here if the parent of this node is not the root.
       */
      if (closingTag.test(node.value)) {
        const childrenToHoist = parent.children.slice(index)
        parent.children.splice(index, childrenToHoist.length)

        // Insert the hoisted children as direct children of the root node
        root.children.splice(
          currentFirstGenChildIndex + 1,
          0,
          ...childrenToHoist,
        )
      }
    })
  }
}
